import React, { useState } from "react";
import { OwnershipRequest } from "../services/ownership.service";
import {
  Button,
  message,
  Pagination,
  Table,
  Tag,
  theme,
  Select,
  Space,
  Modal,
  Card,
  Typography,
  Row,
  Col,
  Divider,
  Tooltip,
} from "antd";
import type { TableProps } from "antd";
import { ReloadOutlined, CopyOutlined } from "@ant-design/icons";
import { formatAddress } from "../utils/formatter";
import { getSolanaExplorerUrl } from "../utils/solana";
import { getNoteFunUrl } from "../utils/ultis";
import {
  useApproveOwnershipRequest,
  useOwnershipRequests,
  useRejectOwnershipRequest,
} from "../hooks/useOwnership";

const renderCopyable = (text: string) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
    <Typography.Text copyable={{ text }}>{text}</Typography.Text>
  </span>
);

const renderLink = (text: string, url: string) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    style={{ wordBreak: "break-all" }}
  >
    {text}
  </a>
);

const OwnershipManagement: React.FC = () => {
  const {
    token: { colorBgContainer, colorBorderSecondary },
  } = theme.useToken();

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<
    "pending" | "approved" | "rejected" | undefined
  >(undefined);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>(
    {}
  );
  const [selectedRequest, setSelectedRequest] =
    useState<OwnershipRequest | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { data, isLoading, refetch, isRefetching } = useOwnershipRequests({
    page,
    limit: 100,
    status,
  });

  const approveMutation = useApproveOwnershipRequest();
  const rejectMutation = useRejectOwnershipRequest();

  const handleApprove = async (id: string) => {
    try {
      setActionLoading((prev) => ({ ...prev, [id]: true }));
      await approveMutation.mutateAsync(id);
      message.success("Ownership request approved successfully");
    } catch (error) {
      message.error("Failed to approve ownership request");
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleReject = async (id: string) => {
    try {
      setActionLoading((prev) => ({ ...prev, [id]: true }));
      await rejectMutation.mutateAsync(id);
      message.success("Ownership request rejected successfully");
    } catch (error) {
      message.error("Failed to reject ownership request");
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  const showDetailModal = (record: OwnershipRequest) => {
    setSelectedRequest(record);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedRequest(null);
  };

  const columns: TableProps<OwnershipRequest>["columns"] = [
    {
      title: "",
      dataIndex: "_id",
      key: "index",
      render: (_: string, __: OwnershipRequest, index: number) =>
        index + 1 + (page - 1) * 100,
    },
    {
      title: "Token",
      dataIndex: "token",
      key: "token",
      render: (value: string, record: OwnershipRequest) => (
        <a
          href={getNoteFunUrl({
            token: value,
            chainId: record.chainId,
          })}
          target="_blank"
          rel="noopener noreferrer"
        >
          {formatAddress(value)}
        </a>
      ),
    },
    {
      title: "Wallet Address",
      dataIndex: "walletAddress",
      key: "walletAddress",
      render: (value: string, record: OwnershipRequest) => (
        <a
          href={getSolanaExplorerUrl({
            path: "account",
            value,
            chainId: record.chainId,
          })}
          target="_blank"
          rel="noopener noreferrer"
        >
          {formatAddress(value)}
        </a>
      ),
    },
    {
      title: () => (
        <Space>
          <span>Status</span>
          <Select
            placeholder="Filter"
            allowClear
            style={{ width: 100 }}
            onChange={(value) => setStatus(value)}
            value={status}
            size="small"
            options={[
              { value: "pending", label: "Pending" },
              { value: "approved", label: "Approved" },
              { value: "rejected", label: "Rejected" },
            ]}
          />
        </Space>
      ),
      dataIndex: "status",
      key: "status",
      render: (value: string) => {
        const colorMap = {
          pending: "gold",
          approved: "green",
          rejected: "red",
        };
        return (
          <Tag
            color={colorMap[value as keyof typeof colorMap]}
            style={{
              textTransform: "capitalize",
            }}
          >
            {value}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: OwnershipRequest) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            type="default"
            onClick={() => showDetailModal(record)}
            size="small"
          >
            Detail
          </Button>
          {record.status === "pending" ? (
            <>
              <Button
                type="primary"
                onClick={() => handleApprove(record._id)}
                loading={actionLoading[record._id]}
                size="small"
              >
                Approve
              </Button>
              <Button
                danger
                onClick={() => handleReject(record._id)}
                loading={actionLoading[record._id]}
                size="small"
              >
                Reject
              </Button>
            </>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="relative">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: colorBgContainer,
          borderBottom: `1px solid ${colorBorderSecondary}`,
        }}
      >
        <h2>Ownership Requests</h2>
      </div>

      <div
        style={{
          padding: "16px 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>{data?.totalDocs || 0} requests found</span>
          <Button
            icon={<ReloadOutlined style={{ fontSize: 12, marginBottom: 2 }} />}
            type="default"
            size="small"
            onClick={() => refetch()}
            loading={isRefetching}
          >
            Refresh
          </Button>
        </div>

        <Pagination
          current={page}
          total={data?.totalDocs}
          pageSize={100}
          onChange={(page) => setPage(page)}
        />
      </div>

      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span>Request Details</span>
            {selectedRequest && (
              <Tag
                color={
                  {
                    pending: "gold",
                    approved: "green",
                    rejected: "red",
                  }[selectedRequest.status]
                }
                style={{ textTransform: "capitalize" }}
              >
                {selectedRequest.status}
              </Tag>
            )}
          </div>
        }
        open={isModalVisible}
        onCancel={handleModalClose}
        width={600}
        footer={
          selectedRequest?.status === "pending"
            ? [
                <Button
                  key="reject"
                  danger
                  onClick={() => {
                    handleReject(selectedRequest._id);
                    handleModalClose();
                  }}
                  loading={actionLoading[selectedRequest._id]}
                >
                  Reject
                </Button>,
                <Button
                  key="approve"
                  type="primary"
                  onClick={() => {
                    handleApprove(selectedRequest._id);
                    handleModalClose();
                  }}
                  loading={actionLoading[selectedRequest._id]}
                >
                  Approve
                </Button>,
              ]
            : [
                <Button key="close" onClick={handleModalClose}>
                  Close
                </Button>,
              ]
        }
      >
        {selectedRequest && (
          <div style={{ maxHeight: "70vh", overflowY: "auto", fontSize: 15 }}>
            <div style={{ marginBottom: 24 }}>
              <Typography.Text strong style={{ fontSize: 16 }}>
                Description
              </Typography.Text>
              <div style={{ whiteSpace: "pre-line", marginTop: 8 }}>
                {selectedRequest.data.description || "-"}
              </div>
            </div>
            <div style={{ marginBottom: 24 }}>
              <Typography.Text strong style={{ fontSize: 16 }}>
                Takeover Claim
              </Typography.Text>
              <div style={{ whiteSpace: "pre-line", marginTop: 8 }}>
                {selectedRequest.data.takeoverClaim || "-"}
              </div>
            </div>
            <Divider style={{ margin: "16px 0" }} />
            <table style={{ width: "100%", marginBottom: 24 }}>
              <tbody>
                <tr>
                  <td style={{ padding: 8 }}>Website</td>
                  <td style={{ textAlign: "right", padding: 8 }}>
                    {selectedRequest.data?.website
                      ? renderLink(
                          selectedRequest.data.website,
                          selectedRequest.data.website
                        )
                      : "-"}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: 8 }}>Telegram</td>
                  <td style={{ textAlign: "right", padding: 8 }}>
                    {selectedRequest.data?.telegram
                      ? renderLink(
                          selectedRequest.data.telegram,
                          selectedRequest.data.telegram
                        )
                      : "-"}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: 8 }}>Twitter</td>
                  <td style={{ textAlign: "right", padding: 8 }}>
                    {selectedRequest.data?.twitter
                      ? renderLink(
                          selectedRequest.data.twitter,
                          selectedRequest.data.twitter
                        )
                      : "-"}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: 8 }}>Discord</td>
                  <td style={{ textAlign: "right", padding: 8 }}>
                    {selectedRequest.data?.discord
                      ? renderLink(
                          selectedRequest.data.discord,
                          selectedRequest.data.discord
                        )
                      : "-"}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: 8 }}>Docs</td>
                  <td style={{ textAlign: "right", padding: 8 }}>
                    {selectedRequest.data?.docs
                      ? renderLink(
                          selectedRequest.data.docs,
                          selectedRequest.data.docs
                        )
                      : "-"}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: 8 }}>Discord Username</td>
                  <td style={{ textAlign: "right", padding: 8 }}>
                    {renderCopyable(
                      selectedRequest.data?.contact?.discordUsername || ""
                    ) || "-"}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: 8 }}>Telegram Username</td>
                  <td style={{ textAlign: "right", padding: 8 }}>
                    {renderCopyable(
                      selectedRequest.data?.contact?.telegramUsername || ""
                    ) || "-"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      <Table
        rowKey="_id"
        dataSource={data?.docs}
        columns={columns}
        loading={isLoading}
        pagination={false}
      />
    </div>
  );
};

export default OwnershipManagement;
